import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Fade,
  CircularProgress,
  Tooltip
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import DashboardTitle from '../Common/DashboardTitle';
import { ComposedChart, Bar, Line, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';

const CompetitiveKPIDashboard = ({ kpiData, performanceData }) => {
  
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
  }, []);

  const extractMetricKeys = (dataPoint) => {
    if (!dataPoint) return [];
    return Object.keys(dataPoint).filter(key => key !== 'name' && !key.startsWith('__') && !key.startsWith('forecast_'));
  };

  const safeKpiData = Array.isArray(kpiData) ? kpiData : [];
  const isKpiEmpty = safeKpiData.length === 0;
  
  // 최신 데이터 포인트에서 현재 값 추출
  const extractCurrentValue = (chartData) => {
    if (!chartData || chartData.length === 0) return null;
    const latestData = chartData[chartData.length - 1];
    const dataKeys = extractMetricKeys(latestData);
    return dataKeys.length > 0 ? latestData[dataKeys[0]] : null;
  };

  // 성취율 계산
  const calculateAchievementRate = (currentValue, targetValue) => {
    if (!currentValue || !targetValue) return null;
    return Math.round(((currentValue / targetValue) * 100) * 100) / 100;
  };

  // 모든 KPI의 종합 성취율 계산
  const calculateOverallAchievementRate = () => {
    if (safeKpiData.length === 0) return null;
    
    const rates = safeKpiData.map(kpi => {
      const currentValue = extractCurrentValue(kpi.chartData);
      return calculateAchievementRate(currentValue, kpi.targetValue);
    }).filter(rate => rate !== null);
    
    if (rates.length === 0) return null;
    return rates.reduce((sum, rate) => sum + rate, 0) / rates.length;
  };
  
  const makeBreakdownKey = (categoryCd, indicatorCd) => {
    return `${categoryCd || 'UNKNOWN'}|${indicatorCd || ''}`;
  };

  const kpiKeyMap = useMemo(() => {
    const map = new Map();
    safeKpiData.forEach((kpi, index) => {
      const label = kpi.kpiTitle || `KPI${index + 1}`;
      map.set(makeBreakdownKey(kpi.categoryCd, kpi.kpiIndicatorCd), label);
    });
    return map;
  }, [safeKpiData]);

  const multiKpiTrendData = useMemo(() => {
    const trend = performanceData?.weeklyTrend;
    if (Array.isArray(trend) && trend.length > 0) {
      const sortedTrend = [...trend].sort((a, b) => new Date(a.date) - new Date(b.date));
      return sortedTrend.map(item => {
        const dateObj = new Date(item.date);
        let label = Number.isNaN(dateObj.getTime())
          ? item.date
          : dateObj.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });

        // 공휴일이면 라벨에 표시 추가
        if (item.isHoliday) {
          label += ' 🔴️';
        }

        const dataPoint = {
          name: label,
          isHoliday: item.isHoliday || false
        };

        if (Array.isArray(item.transmittedKpiData)) {
          item.transmittedKpiData.forEach(breakdown => {
            const breakdownKey = makeBreakdownKey(breakdown.categoryCd, breakdown.kpiIndicatorCd);
            const key = kpiKeyMap.get(breakdownKey) || breakdown.kpiIndicatorNm || breakdown.kpiIndicatorCd;
            if (!key) return;
            const numericRate = Number(breakdown.achievementRate ?? 0);
            dataPoint[key] = Number.isFinite(numericRate) ? numericRate : 0;
          });
        }

        return dataPoint;
      });
    }

    // 실제 데이터가 없으면 빈 배열 반환
    return [];
  }, [performanceData, kpiKeyMap, safeKpiData]);
  
  // KPI별 색상 배열
  const kpiColors = ['#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#00bcd4'];

  // 종합 성과 데이터 계산
  const fallbackAchievementRate = calculateOverallAchievementRate();
  const overallAchievementRate = performanceData?.overallAverageAchievementRate ?? fallbackAchievementRate;

  const currentPosition = performanceData?.branchPosition ?? 0;
  const branchKpiCount = safeKpiData.length;
  const branchDisplayName = '우리 지부에서';
  const positionLabel = currentPosition > 0 ? `#${currentPosition}위` : '데이터 수집중';


  // 성취율에 따른 색상
  const getAchievementColor = (rate) => {
    if (!rate) return '#757575';
    if (rate >= 90) return '#4caf50';
    if (rate >= 75) return '#2196f3';
    if (rate >= 60) return '#ff9800';
    return '#f44336';
  };
  
  // KPI 개수에 따른 색상 할당
  const getKpiColor = (index) => {
    return kpiColors[index % kpiColors.length];
  };

  // 순위별 메달 아이콘
  const getRankIcon = (position) => {
    if (!position || position < 1) {
      return <Typography variant="h6" sx={{ color: '#757575', fontWeight: 'bold' }}>--</Typography>;
    }

    switch (position) {
      case 1:
        return <EmojiEventsIcon sx={{ color: '#ffd700', fontSize: 24 }} />;
      case 2:
        return <WorkspacePremiumIcon sx={{ color: '#c0c0c0', fontSize: 24 }} />;
      case 3:
        return <WorkspacePremiumIcon sx={{ color: '#cd7f32', fontSize: 24 }} />;
      default:
        return <Typography variant="h6" sx={{ color: '#757575', fontWeight: 'bold' }}>#{position}</Typography>;
    }
  };

  if (isKpiEmpty) {
    return (
      <Card sx={{ 
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        bgcolor: '#ffffff',
        border: '2px solid #f0f0f0',
        minHeight: '300px',
        borderRadius: 4
      }}>
        <CardContent sx={{ 
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column'
        }}>
          <EmojiEventsIcon sx={{ fontSize: 64, color: '#bdbdbd', mb: 2 }} />
          <Typography variant="h6" component="div" sx={{ color: '#424242', fontWeight: 600 }}>
            KPI 종합 성과 데이터를 준비 중입니다.
          </Typography>
          <Typography variant="body2" component="div" sx={{ color: '#757575', mt: 1, textAlign: 'center' }}>
            KPI 설정에서 관심 있는 지표를 구독해주세요.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Fade in={visible} timeout={1000}>
      <Card sx={{ 
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        bgcolor: '#ffffff',
        border: `1px solid #f0f0f0`,
        borderRadius: 3,
        background: '#ffffff',
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        
        <CardContent sx={{ 
          p: 2, 
          position: 'relative', 
          zIndex: 1, 
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <DashboardTitle
            icon={EmojiEventsIcon}
            title="KPI 종합 성과 모니터링"
            size="small"
            actions={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`현재 ${branchDisplayName} ${positionLabel}`}
                  icon={getRankIcon(currentPosition)}
                  sx={{
                    bgcolor: currentPosition <= 3 ? '#fff3e0' : '#f5f5f5',
                    color: currentPosition <= 3 ? '#e65100' : '#424242',
                    fontWeight: 'bold',
                    fontSize: { xs: '10px', md: '12px' }
                  }}
                />
                <Tooltip title="공휴일" arrow placement="top">
                  <Chip
                    label="🔴 공휴일"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(244, 67, 54, 0.1)',
                      color: '#f44336',
                      fontWeight: 500,
                      fontSize: { xs: '9px', sm: '10px' },
                      height: { xs: '20px', sm: '24px' },
                      '& .MuiChip-label': {
                        px: { xs: 0.8, sm: 1 }
                      },
                      border: '1px solid rgba(244, 67, 54, 0.3)',
                      cursor: 'help'
                    }}
                  />
                </Tooltip>
              </Box>
            }
          />

          <Grid container spacing={1} sx={{ 
            flex: 1, 
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Left: Circular Progress & Main Stats */}
            <Grid item xs={12} md={4} sx={{ 
              display: 'flex',
              minHeight: { xs: '220px', md: '100%' },
              height: '100%'
            }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                gap: { xs: 0.5, md: 1 },
                overflow: 'visible'
              }}>
                {/* Circular Progress Gauge */}
                <Box sx={{
                  position: 'relative',
                  flexShrink: 0,
                  alignSelf: 'center',
                  width: { xs: '80px', sm: '100px', md: '120px' },
                  height: { xs: '80px', sm: '100px', md: '120px' }
                }}>
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size="100%"
                    thickness={6}
                    sx={{ color: '#e8e8e8' }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={overallAchievementRate ? Math.min(overallAchievementRate, 100) : 0}
                    size="100%"
                    thickness={6}
                    sx={{
                      color: getAchievementColor(overallAchievementRate),
                      position: 'absolute',
                      left: 0,
                      top: 0
                    }}
                  />
                  <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <Typography variant="h5" sx={{
                      fontWeight: 900,
                      color: getAchievementColor(overallAchievementRate),
                      fontSize: { xs: '1rem', md: '1.2rem', lg: '1.5rem' }
                    }}>
                      {overallAchievementRate ? `${overallAchievementRate.toFixed(1)}%` : '0.0%'}
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      color: '#757575', 
                      fontWeight: 600,
                      fontSize: { xs: '0.6rem', md: '0.7rem' },
                      textAlign: 'center',
                      lineHeight: 1.1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%'
                    }}>
                      성취율({branchKpiCount}개)
                    </Typography>
                  </Box>
                </Box>

                {/* KPI Summary Stats - Bottom text only */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: { xs: 0.3, md: 0.5 }, 
                  mb: { xs: 0.5, md: 1 },
                  pt: { xs: 0.5, md: 1 },
                  width: '100%',
                  flex: '0 0 auto'
                }}>
                  <Typography variant="caption" sx={{ 
                    color: '#757575',
                    fontSize: { xs: '0.6rem', md: '0.7rem', lg: '0.75rem' },
                    textAlign: 'center',
                    lineHeight: 1.2
                  }}>
                    전체 {branchKpiCount}개 KPI 지표 대상
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Right: Vertical Bar Chart & Insight */}
            <Grid item xs={12} md={8} sx={{ 
              display: 'flex',
              minHeight: { xs: '250px', md: 'auto' }
            }}>
              <Box sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between',
                overflow: 'hidden',
                width: '100%'
              }}>
                {/* Multi-KPI 7-day trend vertical bar chart */}
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <Typography variant="body1" sx={{
                    color: '#424242',
                    fontWeight: 600,
                    mb: 1,
                    fontSize: { xs: '0.9rem', md: '1rem', lg: '1.1rem' },
                    textAlign: 'center'
                  }}>
                    Open API 전송 7일 추이 (성취율)
                  </Typography>

                  {/* Mixed Chart - Line and Bar Combined */}
                  <Box sx={{
                    height: { xs: '120px', sm: '140px', md: '160px', lg: '180px' },
                    mb: 1,
                    width: '100%'
                  }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={multiKpiTrendData}
                        margin={{
                          top: 20,
                          right: 20,
                          bottom: 20,
                          left: 20
                        }}
                        barCategoryGap="20%"
                      >
                        <XAxis 
                          dataKey="name"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 10, fill: '#666' }}
                        />
                        <YAxis 
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 10, fill: '#666' }}
                          domain={[0, 100]}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            border: '2px solid #6366f1',
                            borderRadius: '8px',
                            fontSize: '12px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                          }}
                          labelStyle={{ fontWeight: 600, color: '#374151' }}
                          formatter={(value, name) => [
                            `${value}%`,
                            name
                          ]}
                        />
                        
                        {/* Render bars for KPIs with chartType 'bar' */}
                        {safeKpiData.filter(kpi => (kpi.chartType?.toLowerCase() || 'bar') === 'bar').map((kpi, index) => {
                          const kpiIndex = safeKpiData.indexOf(kpi);
                          return (
                            <Bar
                              key={`bar-${kpiIndex}`}
                              dataKey={kpi.kpiTitle || `KPI${kpiIndex + 1}`}
                              fill={getKpiColor(kpiIndex)}
                              opacity={0.7}
                              radius={[2, 2, 0, 0]}
                            />
                          );
                        })}
                        
                        {/* Render lines for KPIs with chartType 'line' */}
                        {safeKpiData.filter(kpi => (kpi.chartType?.toLowerCase() || 'bar') === 'line').map((kpi, index) => {
                          const kpiIndex = safeKpiData.indexOf(kpi);
                          return (
                            <Line
                              key={`line-${kpiIndex}`}
                              type="monotone"
                              dataKey={kpi.kpiTitle || `KPI${kpiIndex + 1}`}
                              stroke={getKpiColor(kpiIndex)}
                              strokeWidth={3}
                              dot={{ fill: getKpiColor(kpiIndex), strokeWidth: 2, r: 4 }}
                              activeDot={{ r: 6, stroke: getKpiColor(kpiIndex), strokeWidth: 2 }}
                            />
                          );
                        })}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                  
                  {/* KPI 범례 */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: { xs: 1, md: 1.5 },
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}>
                    {safeKpiData.map((kpi, index) => (
                      <Box key={index} sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 0.8,
                        minWidth: 0
                      }}>
                        <Box sx={{
                          width: { xs: 10, md: 12 },
                          height: { xs: 10, md: 12 },
                          borderRadius: '3px',
                          bgcolor: getKpiColor(index),
                          flexShrink: 0
                        }} />
                        <Typography variant="caption" sx={{ 
                          color: '#666',
                          fontSize: { xs: '10px', md: '11px', lg: '12px' },
                          lineHeight: 1.2,
                          textAlign: 'center',
                          wordBreak: 'keep-all',
                          fontWeight: 500
                        }}>
                          {kpi.kpiTitle || `KPI${index + 1}`}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Fade>
  );
};

export default CompetitiveKPIDashboard;
